/*
  Dasom Skin Scan - Capture Coach.
  Turns the camera step into an INTERACTIVE coach: a face-oval guide + live, plain-language
  guidance ("move closer", "find brighter light", "center your face") so the customer takes a
  genuinely usable shot. The photo is taken ONLY when the customer taps Capture - the coach
  never fires the shutter on its own. When everything lines up, the button lights up and the
  chip invites the tap; the tap grabs a short burst (~5 frames, the "video" read) for a more
  robust on-device analysis. If the precise face detector is unavailable or sees no face,
  the tap is refused (no photos of walls, pets, or empty rooms - ever).

  Three on-device signals, $0, no upload:
    1. MediaPipe FaceLandmarker (lazy-loaded from CDN) -> face presence, distance, centring,
       and the landmark-derived zones for the map. The ONLY signal trusted with the shutter.
    2. Canvas luma -> the lighting checks, live from the first frame. Luma assumes nothing
       about faces, so it is safe to act on before we know whether anyone is there.
    3. Skin-coverage heuristic -> after the detector UX timeout, an advisory face box for
       background/framing diagnostics while the detector continues loading.
       A colour gate cannot tell a face from a warm-toned wall, so on this tier the checks
       are coaching only: they guide the customer, and never open or close the shutter.

  The shutter stays closed until the detector is up and sees a face. If the CDN never
  answers, the customer gets an honest upload/retry path; the timeout never opens capture.

  API:  DasomScanCoach.run({ stage, onResult, onClose })
        onResult(shots, meta) -> shots is an array of dataURLs; meta is { zones } or { zones: null }
*/
window.DasomScanCoach = (function () {
  'use strict';

  var MP_VER = '0.10.18';
  var MP_MODULE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MP_VER + '/vision_bundle.mjs';
  var MP_WASM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MP_VER + '/wasm';
  var MP_MODEL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  // Face-relative zone rects (fraction of the detected face box) used to build the
  // problem-area map tied to the customer's actual face position + size.
  var ZONE_REL = [
    { key: 'forehead', label: 'Forehead', r: [0.20, 0.02, 0.60, 0.18] },
    { key: 'tzone', label: 'T-zone', r: [0.40, 0.28, 0.20, 0.34] },
    { key: 'cheekL', label: 'Left cheek', r: [0.08, 0.42, 0.26, 0.24] },
    { key: 'cheekR', label: 'Right cheek', r: [0.66, 0.42, 0.26, 0.24] },
    { key: 'undereye', label: 'Under-eye', r: [0.20, 0.34, 0.60, 0.08] },
    { key: 'chin', label: 'Chin & jaw', r: [0.30, 0.80, 0.40, 0.18] }
  ];

  // How long to wait before replacing the loading message with a detector-unavailable
  // fallback. This is a UX threshold only: the shutter never opens without a detector.
  //
  // Set from measured cold loads (fresh context, empty cache, throttled profiles):
  // unthrottled median 1.6s with a 3.6s worst case; 4 Mbps 13.0s; 3 Mbps 17.2s;
  // 2 Mbps 25.6s; at or below 1.5 Mbps it did not finish inside 30s. That is a cliff,
  // not a gradient - roughly 13MB of WASM plus model either arrives in a few seconds or
  // takes tens of them. So no value here rescues the middle tier; this one is set just
  // past the fast path's worst case so customers get an honest upload/retry path quickly.
  var MP_TIMEOUT_MS = 4000;

  // Face height as a multiple of the centroid-to-hairline distance, used by
  // faceBoxFromSkin. Named and hoisted here because it is the subject of KIJ-14 and the
  // fit harness reads it back out; see the full provenance note at its use site.
  var FACE_H_RATIO = 1.66;

  // Show the "background may be affecting the scan" tip when more than this fraction of
  // the background passes the skin gate. HAND-TUNED on synthetic scenes: plain wall 0, a
  // small warm picture frame 0.03, a wooden dresser beside the customer 0.22 (heuristic
  // box) / 0.26 (true face box), beige or warm-lit white wall 1.0. 0.25 missed the
  // dresser, whose pixels were 40% of what the engine would have scored.
  //
  // On 1,796 still photos (MediaPipe box) it fires on 64% of a public skin-type set and
  // 85% of a stock/persona set, and still on 13% / 42% at 0.6. Overlays show that is real
  // background - pink and beige walls, warm fabric, white walls with a warm cast - not
  // hair being misread, so a higher threshold only hides it. That is why the tip comes
  // last in the chip order. See backgroundSkinFrac.
  var BG_SKIN_MAX = 0.15;

  var faceApi = null, faceApiPromise = null, faceApiAttempt = 0;
  // Shopify's asset optimizer rewrites a literal dynamic import() into CommonJS require(),
  // which does not exist in the storefront. Build the standards-based import at runtime so
  // the CDN preserves it and the browser can load the pinned MediaPipe ES module.
  function importModule(url) {
    return Function('url', 'return import(url)')(url);
  }
  function loadFaceApi() {
    if (faceApi) return Promise.resolve(faceApi);
    if (faceApiPromise) return faceApiPromise;
    // A failed ES-module fetch is cached as failed by the browser. Give each explicit
    // close/reopen retry a stable new module URL so it performs a real network retry.
    var moduleUrl = MP_MODULE + '?ksa_retry=' + (++faceApiAttempt);
    faceApiPromise = importModule(moduleUrl).then(function (vision) {
      return vision.FilesetResolver.forVisionTasks(MP_WASM).then(function (resolver) {
        return vision.FaceLandmarker.createFromOptions(resolver, {
          baseOptions: { modelAssetPath: MP_MODEL, delegate: 'GPU' },
          runningMode: 'VIDEO', numFaces: 1
        });
      });
    }).then(function (lm) {
      faceApi = lm; faceApiPromise = null; return lm;
    }).catch(function () {
      // Clear the in-flight memo on failure so closing/reopening the scan can retry.
      faceApi = null; faceApiPromise = null; return null;
    });
    return faceApiPromise;
  }

  function canCapture(mpReady, faceOk, box) { return !!mpReady && !!faceOk && !!box; }

  // Bounding box (normalized 0..1) of a face's landmarks.
  function landmarkBox(landmarks) {
    var minX = 1, minY = 1, maxX = 0, maxY = 0;
    for (var i = 0; i < landmarks.length; i++) {
      var p = landmarks[i];
      if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y;
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  // Approximate face box from the skin mask, used once the detector has been given up on:
  // it positions the problem-area map and drives the Face / Distance / Centered checks as
  // coaching. It never decides whether the shutter opens - a colour gate can't be trusted
  // with that, because a beige wall passes it as readily as a cheek.
  //
  // Seeded inside the central region the oval coaches the customer into, so walls and
  // furniture at the frame edges don't count, then re-centred three times on skin within
  // a face-sized window so contaminants beside the face fall out of the average.
  //
  // Height is scaled from the centroid-to-hairline distance: the top of the mask is the
  // only trustworthy boundary (hair above it isn't skin), while the bottom runs straight
  // into neck and chest with no colour edge between them. Coverage fraction and
  // horizontal spread were both measured against MediaPipe's box as ground truth and
  // rejected - each inverted sign between rooms, predicting a smaller face from a wider
  // mask. Clamped to the too-far / too-close band the coach already enforces, so a bad
  // read can't yield an absurd box.
  function faceBoxFromSkin(pts, w, h) {
    var cx0 = w * 0.28, cx1 = w * 0.72, cy0 = h * 0.16, cy1 = h * 0.84;
    var n = 0, sx = 0, sy = 0, i, p, x, y;
    for (i = 0; i < pts.length; i++) {
      p = pts[i]; x = p % w; y = (p - x) / w;
      if (x >= cx0 && x < cx1 && y >= cy0 && y < cy1) { n++; sx += x; sy += y; }
    }
    if (!n) return null;
    var cx = sx / n, cy = sy / n, rx = w * 0.22, ry = h * 0.33;

    for (var it = 0; it < 3; it++) {
      var n2 = 0, sx2 = 0, sy2 = 0;
      for (i = 0; i < pts.length; i++) {
        p = pts[i]; x = p % w; y = (p - x) / w;
        if (Math.abs(x - cx) <= rx && Math.abs(y - cy) <= ry) { n2++; sx2 += x; sy2 += y; }
      }
      if (!n2) break;
      cx = sx2 / n2; cy = sy2 / n2;
    }

    // Top edge of the mask: walk up from the centroid row while the mask stays populated
    // and stop where it breaks, rather than taking the topmost skin row in a window.
    // Hair breaks the run, so this reaches the real hairline with no clipping bound, and
    // it cannot jump the gap to the wall above the head - a painted wall passes the skin
    // gate as readily as a face does, and a bounded search either clips the forehead or
    // finds the wall, with no bound that avoids both. Two consecutive sparse rows end the
    // walk, so one thin row (eyebrows) doesn't. Capped half a frame up so fair hair
    // against a skin-toned wall degrades rather than running away.
    var rows = [];
    for (i = 0; i < h; i++) rows[i] = 0;
    for (i = 0; i < pts.length; i++) {
      p = pts[i]; x = p % w; y = (p - x) / w;
      if (Math.abs(x - cx) <= rx) rows[y]++;
    }
    var mid = Math.max(0, Math.min(h - 1, Math.round(cy)));
    if (!rows[mid]) return null;
    var limit = Math.max(0, Math.round(cy - h * 0.5)), top = mid, miss = 0;
    for (i = mid - 1; i >= limit; i--) {
      if (rows[i] >= rows[mid] * 0.35) { top = i; miss = 0; }
      else if (++miss >= 2) break;
    }

    // Face height = FACE_H_RATIO (1.66) x this centroid-to-hairline distance, with
    // MediaPipe's box height as ground truth. History: 1.76 was fitted on one webcam and
    // one face; a studio set (76 in-band cases, ~5 faces) ran +16.2% signed on it and
    // favoured ~1.50.
    //
    // 1.66 comes from the wider set (INT-2: tests/mp_cases_wider.json, 61 distinct cases,
    // 7 identities, all in-band) by a written rule, not by eye: the lowest pooled height
    // error among multipliers that raise no identity's height error and cost no identity
    // more than 0.03 zone IoU. Height error 25.0% -> 20.9% and lower for every identity;
    // zone IoU 0.277 -> 0.285. The error-only optimum, 1.40, was rejected: it drops the
    // under-eye zone's overlap from 0.25 to 0.06.
    //
    // STILL PROVISIONAL. 69% of the variance in the ideal per-case multiplier is WITHIN
    // an identity, so most of the error is the hairline walk, not this constant; and
    // most of the wider set has skin-toned backgrounds (41 of 61 cases) that the walk
    // climbs. Re-run tests/eval_fallback_wider.js before changing it.
    //
    // 0.78 is face width:height in PIXELS; (h / w) converts that into frame-normalised
    // units, since box w and h are fractions of different frame dimensions. Both clamped
    // to the too-far / too-close bounds the coach already enforces.
    var rawH = FACE_H_RATIO * (cy - top) / h;
    var fh = Math.max(0.42, Math.min(0.86, rawH));
    var fw = fh * 0.78 * (h / w);

    // Vertically the box is anchored on the hairline, not on the centroid: nothing says
    // the skin centroid sits at the middle of a face, and centring on it put every zone
    // high by a consistent margin across three measured sessions. MediaPipe's landmark
    // box starts below the hairline - its topmost points stop short of it - and 0.13 is
    // that gap as a fraction of face height. Expressed relative to face height rather
    // than to the frame so it stays correct at every distance.
    //
    // rawH is the height before the clamp. The Distance check reads it, because the
    // clamped h sits inside the too-far / too-close band by construction and would always
    // pass. The walk's half-frame cap bounds it at about FACE_H_RATIO x 0.5 = 0.83 (0.84
    // with row rounding; measured 0.836 on an all-skin wall), which is BELOW the 0.86
    // too-close line: on this tier Distance can say "too far" but never
    // "too close". (At the earlier 1.76 the bound was 0.88, just past the line.) A
    // skin-toned wall above the head still runs the walk to the cap; it now reads in-band.
    return {
      x: Math.max(0, Math.min(1 - fw, cx / w - fw / 2)),
      y: Math.max(0, Math.min(1 - fh, top / h + 0.13 * fh)),
      w: fw, h: fh, rawH: rawH
    };
  }

  // Fraction of the background that passes the skin gate. The engine scores every pixel in
  // the frame that passes the gate, so a beige wall, a wooden dresser or a warm-lit door
  // behind the customer is scored as if it were their skin. The person is taken as the
  // face box widened for ears and hair, plus a shoulder-width column below the chin for
  // neck and chest; everything else is background. Measured against the background's AREA,
  // not as a share of all gated pixels: on the heuristic tier the box is itself built from
  // the gate, and a beige wall inflates it to the clamp and shrinks the background - a
  // share came out at only 0.29-0.33 on scenes that were almost entirely wall. Returns 0
  // when too little background is in view to judge.
  function backgroundSkinFrac(pts, w, h, fb) {
    var hx0 = (fb.x - 0.2 * fb.w) * w, hx1 = (fb.x + 1.2 * fb.w) * w;
    var hy0 = (fb.y - 0.3 * fb.h) * h, chin = (fb.y + fb.h) * h;
    var mid = (fb.x + fb.w / 2) * w, half = 1.2 * fb.w * w;
    function isPerson(x, y) { return y < chin ? (x >= hx0 && x < hx1 && y >= hy0) : Math.abs(x - mid) <= half; }
    var area = 0, hits = 0, i, p, x, y;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) if (!isPerson(x, y)) area++;
    if (area < w * h * 0.1) return 0;
    for (i = 0; i < pts.length; i++) {
      p = pts[i]; x = p % w; y = (p - x) / w;
      if (!isPerson(x, y)) hits++;
    }
    return hits / area;
  }

  // Convert a face box -> frame-normalized zone rects for the engine's analyzeRegions.
  function zonesFromFaceBox(fb) {
    return ZONE_REL.map(function (z) {
      return { key: z.key, label: z.label, box: [fb.x + z.r[0] * fb.w, fb.y + z.r[1] * fb.h, z.r[2] * fb.w, z.r[3] * fb.h] };
    });
  }

  // Canvas luma + skin analysis over a downscaled frame (the always-on tier).
  // src is normally the live <video>, but any drawable with intrinsic dimensions works -
  // the fit harness feeds still images through this same path so measurements can't drift
  // from what actually ships.
  function readFrame(src, scratch) {
    var sw = src.videoWidth || src.naturalWidth || src.width;
    var sh = src.videoHeight || src.naturalHeight || src.height;
    var w = 160, h = Math.max(1, Math.round((sh / sw) * 160)) || 120;
    scratch.width = w; scratch.height = h;
    var ctx = scratch.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(src, 0, 0, w, h);
    var d;
    try { d = ctx.getImageData(0, 0, w, h).data; } catch (e) { return null; }
    // Central region where a framed face should sit.
    var cx0 = w * 0.28, cx1 = w * 0.72, cy0 = h * 0.16, cy1 = h * 0.84;
    var sumC = 0, nC = 0, sumL = 0, nL = 0, sumR = 0, nR = 0, sumBg = 0, nBg = 0;
    var skinN = 0, pts = [];
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var p = (y * w + x) * 4, r = d[p], g = d[p + 1], b = d[p + 2];
        var yv = 0.299 * r + 0.587 * g + 0.114 * b;
        var inC = x >= cx0 && x < cx1 && y >= cy0 && y < cy1;
        if (inC) { sumC += yv; nC++; if (x < w * 0.5) { sumL += yv; nL++; } else { sumR += yv; nR++; } }
        else { sumBg += yv; nBg++; }
        // skin gate (same as the engine)
        var cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
        var cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
        if (r > 50 && g > 25 && b > 12 && cr >= 128 && cr <= 188 && cb >= 70 && cb <= 138) { skinN++; pts.push(y * w + x); }
      }
    }
    var faceLuma = nC ? sumC / nC : 0;
    return {
      faceLuma: faceLuma,
      lr: (nL && nR) ? Math.abs(sumL / nL - sumR / nR) / (faceLuma || 1) : 0,
      backlit: (nBg ? sumBg / nBg : 0) - faceLuma,
      // skinFrac gates whether a box is worth attempting - it is NOT a face check. The
      // skin centroid that used to be returned here went with the face claims it fed.
      skinFrac: skinN / (w * h),
      pts: pts, mw: w, mh: h
    };
  }

  function run(opts) {
    var stage = opts.stage;
    var stream = null, video = null, loop = null, scratch = document.createElement('canvas');
    var lastBox = null, lastFaceOk = false, capturing = false, done = false, lastChip = '';
    var mpDeadline = Infinity; // set once the camera is live; see MP_TIMEOUT_MS

    stage.innerHTML =
      '<p class="dss-eyebrow">Dasom Skin Scan</p>' +
      '<h3 class="dss-h">Let’s get a clear read</h3>' +
      '<div class="dss-view" id="dssView">' +
        '<div class="dss-oval" id="dssOval"></div>' +
        '<div class="dss-coachchip" id="dssChip">Starting camera…</div>' +
      '</div>' +
      '<div class="dss-checks" id="dssChecks">' +
        '<span class="dss-chk" data-k="face">Face</span>' +
        '<span class="dss-chk" data-k="dist">Distance</span>' +
        '<span class="dss-chk" data-k="center">Centered</span>' +
        '<span class="dss-chk" data-k="light">Lighting</span>' +
      '</div>' +
      '<div class="dss-actions">' +
        '<button class="dss-btn" id="dssShot" disabled>Capture</button>' +
        '<button class="dss-btn ghost" id="dssBack">Close</button>' +
      '</div>' +
      '<label class="dss-upload">or upload a photo instead<input type="file" accept="image/*" id="dssFile" multiple hidden></label>' +
      '<p class="dss-priv">&#128274; Your scan is analyzed on your device. Nothing is uploaded.</p>';

    var view = stage.querySelector('#dssView');
    var oval = stage.querySelector('#dssOval');
    var chip = stage.querySelector('#dssChip');
    var shotBtn = stage.querySelector('#dssShot');
    var checksEl = stage.querySelector('#dssChecks');
    var file = stage.querySelector('#dssFile');

    function setChip(t) { if (t !== lastChip) { chip.textContent = t; lastChip = t; } }
    function setCheck(k, ok) { var el = checksEl.querySelector('[data-k="' + k + '"]'); if (el) el.classList.toggle('ok', !!ok); }

    function stop() {
      if (loop) { clearInterval(loop); loop = null; }
      if (stream) { stream.getTracks().forEach(function (t) { t.stop(); }); stream = null; }
    }
    function close() { if (done) return; done = true; stop(); if (opts.onClose) opts.onClose(); }
    function finish(shots, zones) {
      if (done) return; done = true; stop();
      opts.onResult(shots, { zones: zones || null });
    }

    stage.querySelector('#dssBack').onclick = close;

    // Upload fallback.
    stage.querySelector('.dss-upload').onclick = function (e) { if (e.target.tagName !== 'INPUT') file.click(); };
    file.onchange = function () {
      if (!file.files.length) return;
      var imgs = [], total = file.files.length;
      Array.prototype.slice.call(file.files).forEach(function (f) {
        var rd = new FileReader();
        rd.onload = function () { imgs.push(rd.result); if (imgs.length === total) finish(imgs, null); };
        rd.readAsDataURL(f);
      });
    };

    // Capture a full-resolution burst from the live video, then finish.
    // Only ever called from the customer's explicit Capture tap - never automatically.
    function captureBurst() {
      if (capturing || done) return;
      // Fail closed. A colour/skin heuristic cannot distinguish a face from a warm wall,
      // so a missing detector is never permission to photograph or score the frame.
      if (!canCapture(faceApi, lastFaceOk, lastBox)) {
        setChip(faceApi ? 'We can’t see your face yet. Line up in the oval first' :
          'Face check unavailable. Use “upload a photo” below, or close and reopen to retry');
        return;
      }
      capturing = true;
      if (loop) { clearInterval(loop); loop = null; }
      // The shutter only opens once the detector has supplied this box.
      var fb = lastBox;
      var zones = fb ? zonesFromFaceBox(fb) : null;
      var shots = [], frames = 5, taken = 0;
      view.classList.add('flash');
      setTimeout(function () { view.classList.remove('flash'); }, 160);
      setChip('Got it. Analyzing…');
      (function grab() {
        if (done) return;
        var c = document.createElement('canvas');
        c.width = video.videoWidth || 640; c.height = video.videoHeight || 480;
        c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
        shots.push(c.toDataURL('image/jpeg', 0.85));
        taken++;
        if (taken >= frames) { finish(shots, zones); return; }
        setTimeout(grab, 150);
      })();
    }
    shotBtn.onclick = captureBurst;

    // Evaluate one frame, drive the chip + checks, and auto-capture when aligned.
    function tick() {
      if (!video || video.readyState < 2 || capturing || done) return;
      var f = readFrame(video, scratch);
      if (!f) return;

      // Lighting, from the luma tier. Live from the first frame and independent of whether
      // the detector has loaded.
      var tooDark = f.faceLuma < 85, tooBright = f.faceLuma > 215;
      var uneven = f.lr > 0.32, backlit = f.backlit > 48;
      var lightOk = !tooDark && !tooBright && !uneven && !backlit;
      setCheck('light', lightOk);

      var mpReady = !!faceApi;
      var mpTimedOut = !mpReady && Date.now() > mpDeadline;

      // Face presence, distance and centring, read off one box with the same thresholds on
      // both tiers: the detector's landmark box, or after the timeout the heuristic's skin
      // box. The heuristic's answers are coaching only - its colour gate reports all-clear
      // on an empty room against a beige wall - so they never reach the shutter. While the
      // detector loads these stay unlit.
      var faceOk = false, distOk = false, centerOk = false, tooFar = false, tooClose = false;
      lastBox = null;
      if (mpReady) {
        var res = null;
        try { res = faceApi.detectForVideo(video, performance.now()); } catch (e) { res = null; }
        var lm = res && res.faceLandmarks && res.faceLandmarks[0];
        if (lm) lastBox = landmarkBox(lm);
      } else if (mpTimedOut) {
        lastBox = f.skinFrac > 0.07 ? faceBoxFromSkin(f.pts, f.mw, f.mh) : null;
      }
      if (lastBox) {
        faceOk = true;
        // The heuristic box's h is clamped into the band, so judge its pre-clamp height.
        var bh = lastBox.rawH != null ? lastBox.rawH : lastBox.h;
        tooFar = bh < 0.42; tooClose = bh > 0.86;
        distOk = !tooFar && !tooClose;
        var cx = lastBox.x + lastBox.w / 2, cy = lastBox.y + lastBox.h / 2;
        centerOk = Math.abs(cx - 0.5) < 0.13 && Math.abs(cy - 0.52) < 0.16;
      }

      // Background the skin gate mistakes for skin. Coaching only, like the checks above.
      var busyBg = !!lastBox && backgroundSkinFrac(f.pts, f.mw, f.mh, lastBox) > BG_SKIN_MAX;

      // captureBurst()'s face guard trusts the detector only. Without mpReady here, a tap
      // in the tick after MediaPipe finishes loading would pass on the heuristic's say-so.
      lastFaceOk = mpReady && faceOk;
      setCheck('face', mpReady && faceOk);
      setCheck('dist', mpReady && distOk);
      setCheck('center', mpReady && centerOk);

      // Shutter and ready styling both fail closed until MediaPipe sees a face.
      shotBtn.disabled = !canCapture(mpReady, faceOk, lastBox);
      oval.className = 'dss-oval' + (mpReady && faceOk && distOk && centerOk && lightOk && !busyBg ? ' ready' : (mpReady && faceOk ? ' near' : ''));

      // Single most-important instruction (priority order).
      var msg;
      if (!mpReady && !mpTimedOut) msg = 'Getting the camera ready…';
      else if (!mpReady) msg = 'Face check unavailable. Use “upload a photo” below, or close and reopen to retry';
      else if (!faceOk) msg = 'Bring your face into the oval';
      else if (tooDark) msg = 'Find brighter light. Face a window';
      else if (backlit) msg = 'Turn away from the light behind you';
      else if (tooBright) msg = 'Soften the light a little';
      else if (uneven) msg = 'Even out the light, avoid side light';
      else if (tooFar) msg = 'Move a little closer';
      else if (tooClose) msg = 'Move back a little';
      else if (!centerOk) msg = 'Center your face in the oval';
      // Last, so it never hides framing advice: real backgrounds pass the skin gate often
      // enough (see BG_SKIN_MAX) that ahead of distance it would have replaced "move
      // closer / back" for most badly framed faces. On the heuristic tier a skin-toned wall
      // runs the hairline walk to its cap, which at FACE_H_RATIO 1.66 reads in-band, so the
      // customer reaches this tip (at 1.76 they were told "Move back a little" instead).
      else if (busyBg) msg = 'Your background may be affecting the scan. Try a spot with a plain wall behind you';
      else msg = null; // all good

      if (msg) { shotBtn.classList.remove('ready'); setChip(msg); return; }

      // Aligned + well-lit: light up the button and wait for the customer's tap. The coach
      // NEVER takes the photo on its own.
      shotBtn.classList.add('ready');
      setChip('Looking good. Tap Capture when you’re ready');
    }

    // Start the detector download BEFORE asking for the camera. The permission prompt plus
    // camera warm-up is several seconds on a first visit and we used to spend all of it
    // downloading nothing, because this call sat inside the getUserMedia callback. Cold
    // loads measure 1.6s on broadband and tens of seconds below 4 Mbps, so a few seconds
    // of head start is worth having. Memoised, so re-opening the scan doesn't refetch.
    // mpDeadline still starts when the camera is live, so this only ever gives MediaPipe
    // more time, never less.
    loadFaceApi();

    // Start the camera, then the loop. Lighting coaching runs immediately; the face checks
    // and the shutter wait for the detector. MP_TIMEOUT_MS changes the message only.
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 } }, audio: false }).then(function (s) {
        if (done) { s.getTracks().forEach(function (t) { t.stop(); }); return; }
        stream = s;
        video = document.createElement('video');
        video.autoplay = true; video.playsInline = true; video.muted = true; video.srcObject = s;
        view.insertBefore(video, oval);
        setChip('Getting the camera ready…');
        shotBtn.disabled = true;   // tick() opens it only after the detector sees a face
        mpDeadline = Date.now() + MP_TIMEOUT_MS;
        loop = setInterval(tick, 120);
      }).catch(function () {
        setChip('Camera unavailable. Use “upload a photo” below');
        oval.style.display = 'none';
      });
    } else {
      setChip('Camera not supported here. Use “upload a photo” below');
      oval.style.display = 'none';
    }

    return { close: close };
  }

  // Test seam. The fit harness measures faceBoxFromSkin against MediaPipe ground truth to
  // re-fit its height constant, and a harness that reimplements the algorithm drifts from
  // the shipped one - which has already happened once here and cost a day of chasing a
  // discrepancy that did not exist. Exposing the functions is cheaper than that risk; the
  // background measure is here so BG_SKIN_MAX can be checked in a real room the same way.
  // Nothing in production reads _test.
  return {
    run: run,
    _test: { readFrame: readFrame, faceBoxFromSkin: faceBoxFromSkin, FACE_H_RATIO: FACE_H_RATIO,
             backgroundSkinFrac: backgroundSkinFrac, BG_SKIN_MAX: BG_SKIN_MAX,
             canCapture: canCapture }
  };
})();
